import { lazy, type ReactNode, useEffect } from "react";

import { hasPermission, type Permission } from "@mongmee/domain";

import { useAuth } from "./auth";
import { ActiveBrandProvider } from "./active-brand";
import { useLocale } from "./i18n";
import { tenantPagePermission } from "./navigation-policy";
import { Navigate, ParamsProvider, useLocation } from "./router";

const ActivatePage = lazy(() => import("./pages/ActivatePage").then((module) => ({ default: module.ActivatePage })));
const AuditPage = lazy(() => import("./pages/AuditPage").then((module) => ({ default: module.AuditPage })));
const BrandsPage = lazy(() => import("./pages/BrandsPage").then((module) => ({ default: module.BrandsPage })));
const ContentApprovalsPage = lazy(() => import("./pages/ContentApprovalsPage").then((module) => ({ default: module.ContentApprovalsPage })));
const ContentCalendarPage = lazy(() => import("./pages/ContentCalendarPage").then((module) => ({ default: module.ContentCalendarPage })));
const ContentEditorPage = lazy(() => import("./pages/ContentEditorPage").then((module) => ({ default: module.ContentEditorPage })));
const ContentHistoryPage = lazy(() => import("./pages/ContentHistoryPage").then((module) => ({ default: module.ContentHistoryPage })));
const ContentInboxPage = lazy(() => import("./pages/ContentInboxPage").then((module) => ({ default: module.ContentInboxPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })));
const HomePage = lazy(() => import("./pages/HomePage").then((module) => ({ default: module.HomePage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then((module) => ({ default: module.SignupPage })));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage").then((module) => ({ default: module.SubscriptionPage })));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage").then((module) => ({ default: module.ForgotPasswordPage })));
const MfaPage = lazy(() => import("./pages/MfaPage").then((module) => ({ default: module.MfaPage })));
const NoAccessPage = lazy(() => import("./pages/NoAccessPage").then((module) => ({ default: module.NoAccessPage })));
const PlatformPage = lazy(() => import("./pages/PlatformPage").then((module) => ({ default: module.PlatformPage })));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage").then((module) => ({ default: module.OnboardingPage })));
const ResearchPage = lazy(() => import("./pages/ResearchPage").then((module) => ({ default: module.ResearchPage })));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage").then((module) => ({ default: module.ResetPasswordPage })));
const TeamPage = lazy(() => import("./pages/TeamPage").then((module) => ({ default: module.TeamPage })));
const AiSettingsPage = lazy(() => import("./pages/AiSettingsPage").then((module) => ({ default: module.AiSettingsPage })));
const ChannelsPage = lazy(() => import("./pages/ChannelsPage").then((module) => ({ default: module.ChannelsPage })));
const AutomationSettingsPage = lazy(() => import("./pages/AutomationSettingsPage").then((module) => ({ default: module.AutomationSettingsPage })));
const DataRightsPage = lazy(() => import("./pages/DataRightsPage").then((module) => ({ default: module.DataRightsPage })));
const AccountSecurityPage = lazy(() => import("./pages/AccountSecurityPage").then((module) => ({ default: module.AccountSecurityPage })));
const KnowledgePage = lazy(() => import("./pages/KnowledgePage").then((module) => ({ default: module.KnowledgePage })));
const ContentOperationsPage = lazy(() => import("./pages/ContentOperationsPage").then((module) => ({ default: module.ContentOperationsPage })));
const AppShell = lazy(() => import("./components/AppShell").then((module) => ({ default: module.AppShell })));
const PlatformShell = lazy(() => import("./components/PlatformShell").then((module) => ({ default: module.PlatformShell })));

export function App() {
  const { initializationError, loading } = useAuth();
  const { locale, t } = useLocale();
  const location = useLocation();

  useEffect(() => {
    document.title = `${routeTitle(location.pathname, location.search, locale, t)} · ${t("appName")}`;
  }, [locale, location.pathname, location.search, t]);

  // Keep the public site available during an API outage. HomePage performs its
  // own capability check and degrades to the assisted-pilot CTA when needed.
  if (location.pathname === "/") return <HomePage />;

  if (loading) return <div className="boot-screen">{t("loading")}</div>;
  if (initializationError) {
    return (
      <div className="boot-screen">
        <div className="content-card service-error">
          <h1>{t("serviceUnavailable")}</h1>
          <p>{t("requestFailed")}</p>
          <button
            className="button button-primary"
            type="button"
            onClick={() => window.location.reload()}
          >
            {t("retry")}
          </button>
        </div>
      </div>
    );
  }
  if (location.pathname === "/login") return <LoginPage audience="customer" />;
  if (location.pathname === "/signup") return <SignupPage />;
  if (location.pathname === "/platform/login") return <LoginPage audience="platform" />;
  if (location.pathname === "/activate") return <ActivatePage />;
  if (location.pathname === "/forgot-password") return <ForgotPasswordPage />;
  if (location.pathname === "/reset-password") return <ResetPasswordPage />;
  if (location.pathname === "/mfa") return <MfaPage />;
  if (location.pathname === "/platform/mfa") return <MfaPage />;
  if (location.pathname === "/no-access") {
    return <Protected><AppShell><NoAccessPage /></AppShell></Protected>;
  }
  if (location.pathname === "/platform") {
    return <PlatformProtected><PlatformShell><PlatformPage /></PlatformShell></PlatformProtected>;
  }
  if (location.pathname === "/platform/account/security") {
    return <PlatformProtected><PlatformShell><AccountSecurityPage /></PlatformShell></PlatformProtected>;
  }
  if (location.pathname === "/account/security") {
    return <Protected><AppShell><AccountSecurityPage /></AppShell></Protected>;
  }

  const contentEditorRoute = /^\/t\/([0-9a-f]{8}-[0-9a-f-]{27})\/content\/([0-9a-f]{8}-[0-9a-f-]{27})\/?$/iu.exec(
    location.pathname,
  );
  if (contentEditorRoute?.[1] && contentEditorRoute[2]) {
    return (
      <ParamsProvider
        params={{ tenantId: contentEditorRoute[1], briefId: contentEditorRoute[2] }}
      >
        <Protected>
          <ActiveBrandProvider tenantId={contentEditorRoute[1]}>
            <AppShell>
              <TenantPermissionProtected tenantId={contentEditorRoute[1]} permission="content.read">
                <ContentEditorPage />
              </TenantPermissionProtected>
            </AppShell>
          </ActiveBrandProvider>
        </Protected>
      </ParamsProvider>
    );
  }

  const tenantRoute = /^\/t\/([0-9a-f]{8}-[0-9a-f-]{27})\/(dashboard|onboarding|automation-settings|brands|team|audit|content|approvals|calendar|content-history|content-operations|ai-settings|research|knowledge|channels|data-rights|subscription)\/?$/iu.exec(
    location.pathname,
  );
  if (tenantRoute?.[1] && tenantRoute[2]) {
    const tenantId = tenantRoute[1];
    const page = {
      dashboard: <DashboardPage />,
      subscription: <SubscriptionPage />,
      onboarding: <OnboardingPage />,
      "automation-settings": <AutomationSettingsPage />,
      brands: <BrandsPage />,
      team: <TeamPage />,
      audit: <AuditPage />,
      content: <ContentInboxPage />,
      approvals: <ContentApprovalsPage />,
      calendar: <ContentCalendarPage />,
      "content-history": <ContentHistoryPage />,
      "ai-settings": <AiSettingsPage />,
      research: <ResearchPage />,
      knowledge: <KnowledgePage />,
      "content-operations": <ContentOperationsPage />,
      channels: <ChannelsPage />,
      "data-rights": <DataRightsPage />,
    }[tenantRoute[2]];
    const permission = tenantPagePermission(tenantRoute[2]);
    const guardedPage = <TenantPermissionProtected tenantId={tenantId} permission={permission}>{page}</TenantPermissionProtected>;
    return (
      <ParamsProvider params={{ tenantId }}>
        <Protected>
          <ActiveBrandProvider tenantId={tenantId}>
            <AppShell>{guardedPage}</AppShell>
          </ActiveBrandProvider>
        </Protected>
      </ParamsProvider>
    );
  }
  return <Navigate to="/" replace />;
}

function routeTitle(
  pathname: string,
  search: string,
  locale: "lo-LA" | "th-TH",
  t: (key: Parameters<ReturnType<typeof useLocale>["t"]>[0]) => string,
): string {
  const isLao = locale === "lo-LA";
  if (pathname === "/") return isLao ? "ຜູ້ຊ່ວຍເນື້ອຫາທຸລະກິດ" : "ผู้ช่วยจัดการเนื้อหาธุรกิจ";
  if (pathname === "/login") return t("login");
  if (pathname === "/signup") return isLao ? "ສ້າງບັນຊີ" : "สร้างบัญชี";
  if (pathname === "/platform/login") return isLao ? "ເຂົ້າສູ່ສ່ວນຜູ້ດູແລ" : "เข้าสู่ส่วนผู้ดูแล";
  if (pathname === "/activate") return t("activation");
  if (pathname === "/forgot-password") return isLao ? "ລືມລະຫັດຜ່ານ" : "ลืมรหัสผ่าน";
  if (pathname === "/reset-password") return isLao ? "ຕັ້ງລະຫັດຜ່ານໃໝ່" : "ตั้งรหัสผ่านใหม่";
  if (pathname === "/mfa") return isLao ? "ການຢືນຢັນສອງຂັ້ນຕອນ" : "การยืนยันสองขั้นตอน";
  if (pathname === "/platform/mfa") return isLao ? "ການຢືນຢັນຜູ້ດູແລ" : "การยืนยันผู้ดูแล";
  if (pathname === "/platform") {
    const section = new URLSearchParams(search).get("section");
    const titles = {
      customers: locale === "lo-LA" ? "ລູກຄ້າ ແລະ ແພັກເກດ" : "ลูกค้าและแพ็กเกจ",
      privacy: locale === "lo-LA" ? "ຂໍ້ມູນ ແລະ ຄວາມປອດໄພ" : "ข้อมูลและความปลอดภัย",
      support: locale === "lo-LA" ? "ການຊ່ວຍເຫຼືອລູກຄ້າ" : "การช่วยเหลือลูกค้า",
    } as Record<string, string>;
    return titles[section ?? ""] ?? (locale === "lo-LA" ? "ພາບລວມລະບົບ" : "ภาพรวมระบบ");
  }
  if (pathname === "/account/security" || pathname === "/platform/account/security") {
    return isLao ? "ຄວາມປອດໄພບັນຊີ" : "ความปลอดภัยบัญชี";
  }
  if (pathname === "/no-access") {
    return isLao ? "ຍັງບໍ່ມີສິດເຂົ້າໃຊ້" : "ยังไม่มีสิทธิ์เข้าใช้งาน";
  }
  if (/\/content\/[0-9a-f-]+\/?$/iu.test(pathname)) return t("editor");
  const routeSegment = pathname.split("/").filter(Boolean).at(-1);
  return ({
    dashboard: t("dashboard"),
    onboarding: t("onboarding"),
    "automation-settings": t("automationSettings"),
    brands: t("brands"),
    team: t("team"),
    audit: t("audit"),
    content: t("content"),
    approvals: t("approvals"),
    calendar: t("calendar"),
    "content-history": t("contentHistory"),
    "ai-settings": isLao ? "ຕັ້ງຄ່າ AI" : "ตั้งค่า AI",
    research: t("research"),
    knowledge: isLao ? "ຄັງຄວາມຮູ້" : "คลังความรู้",
    "content-operations": isLao ? "ການຈັດການເນື້ອຫາ" : "การจัดการเนื้อหา",
    channels: t("channels"),
    subscription: isLao ? "ສະມາຊິກ ແລະການຊຳລະ" : "สมาชิกและการชำระ",
    "data-rights": isLao ? "ຂໍ້ມູນ ແລະ ການປິດ" : "ข้อมูลและการปิด",
  } as Record<string, string>)[routeSegment ?? ""] ?? t("appName");
}

function Protected({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const location = useLocation();
  const destination = `${location.pathname}${location.search}`;
  if (!session) return <Navigate to="/login" state={{ from: destination }} replace />;
  if (["enrollment_required", "challenge_required"].includes(session.mfa.status)) {
    return <Navigate to="/mfa" state={{ from: destination }} replace />;
  }
  return children;
}

function PlatformProtected({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const location = useLocation();
  const destination = `${location.pathname}${location.search}`;
  if (!session) return <Navigate to="/platform/login" state={{ from: destination }} replace />;
  if (["enrollment_required", "challenge_required"].includes(session.mfa.status)) {
    return <Navigate to="/platform/mfa" state={{ from: destination }} replace />;
  }
  if (!session.platformRoles.includes("platform_admin")) return <Navigate to="/no-access" replace />;
  return children;
}

function TenantPermissionProtected({
  children,
  permission,
  tenantId,
}: {
  children: ReactNode;
  permission: Permission;
  tenantId: string;
}) {
  const { session } = useAuth();
  if (!session) return <Navigate to="/login" replace />;
  const tenant = session.tenants.find((candidate) => candidate.id === tenantId);
  if (!tenant || !hasPermission(tenant.roles, permission)) {
    return <Navigate to="/no-access" replace />;
  }
  return children;
}
